import { configs } from '@constants/configs/configs';
import { PAGE_SIZE } from '@constants/common.constants';
import { CAP_HISTORICAL_DOCUMENTS_ACTION_TYPES } from '@constants/actionTypes/capHistoricalDocumentsActionTypes';
import { createPaginatedFetchThunk } from '@common-actions/createPaginatedFetchThunk';
import {
    HISTORICAL_DOCUMENTS_DEFAULT_SORT_COLUMN,
    HISTORICAL_DOCUMENTS_DEFAULT_SORT_ORDER,
    HISTORICAL_DOCUMENTS_INITIAL_PAGE_NUMBER,
    HISTORICAL_DOCUMENTS_LIST_KEY,
    type IHistoricalDocumentsPageData,
} from '@modules/historical-documents/constants';
import type {
    ICapHistoricalDocumentsFetchRequest,
    ICapHistoricalDocumentsListState,
} from '@modules/historical-documents/cap/cap.types';

const buildBasePayload = (request: ICapHistoricalDocumentsFetchRequest) => ({
    hasLoaded: true,
    documents: [],
    totalCount: 0,
    pageNumber: request.pageNumber,
    pageSize: request.pageSize,
    totalPages: 0,
    sortColumn: request.sortColumn,
    sortOrder: request.sortOrder,
});

export const setCapHistoricalDocuments = (payload: ICapHistoricalDocumentsListState) => ({
    type: CAP_HISTORICAL_DOCUMENTS_ACTION_TYPES.SET_CAP_HISTORICAL_DOCUMENTS,
    payload,
});

export const resetCapHistoricalDocuments = () => ({
    type: CAP_HISTORICAL_DOCUMENTS_ACTION_TYPES.RESET_CAP_HISTORICAL_DOCUMENTS,
});

export const fetchCapHistoricalDocuments = createPaginatedFetchThunk<
    ICapHistoricalDocumentsFetchRequest,
    IHistoricalDocumentsPageData,
    typeof HISTORICAL_DOCUMENTS_LIST_KEY,
    ICapHistoricalDocumentsListState
>({
    getEndpointUrl: () => configs.endpoints.HISTORICAL_DOCUMENTS,
    listKey: HISTORICAL_DOCUMENTS_LIST_KEY,
    setList: setCapHistoricalDocuments,
    requestDefaults: {
        pageNumber: HISTORICAL_DOCUMENTS_INITIAL_PAGE_NUMBER,
        pageSize: PAGE_SIZE,
        sortColumn: HISTORICAL_DOCUMENTS_DEFAULT_SORT_COLUMN,
        sortOrder: HISTORICAL_DOCUMENTS_DEFAULT_SORT_ORDER,
    },
    // Pagination + sort params (pageNumber, pageSize, sortColumn, sortOrder) are
    // appended to the query string automatically by createPaginatedFetchThunk.
    buildQueryParams: (request) => ({ userRefId: request.userRefId ?? '' }),
    buildEmptyPayload: (request) => ({
        ...buildBasePayload(request),
        hasLoadError: false,
    }),
    buildErrorPayload: (request) => ({
        ...buildBasePayload(request),
        hasLoadError: true,
    }),
    buildSuccessPayload: (pageData, request) => ({
        ...buildBasePayload(request),
        documents: pageData.userHistoricalDocuments,
        totalCount: pageData.totalElements,
        pageNumber: pageData.pageNumber,
        pageSize: pageData.pageSize,
        totalPages: pageData.totalPages,
        hasLoadError: false,
    }),
});
